"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar, Plus } from "lucide-react";
import { toast } from "react-hot-toast";
import moment, { defaultDateRange, startOfDay, endOfDay, isDateTimeInRange, formatInAppTz, parseAppDateTime } from "../utils/timezone";
import { employeeManagementService } from '../apis/employeeManagement';
import { waitresstipoutService } from "../apis/waitresstipoutService";
import DateRangePicker from "../components/ui/date-range-picker";
import { useAuth } from "../context/AuthContext";
import { WaitressTipAddModal } from "../components/waitress-tip-add-modal";
import { WaitressTipEditModal } from "../components/waitress-tip-edit-modal";
import { WaitressTipDeleteModal } from "../components/waitress-tip-delete-modal";
import { WaitressTipsTransactionTable } from "../components/waitress-tips-transaction-table";
import { WaitressTipActivityLogPanel } from "../components/waitress-tip-activity-log-panel";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EmployeeTotalsTable } from "../components/employee-totals-table";
import { calculateTipCommission, TIP_ROLES, getTipCommissionLabel } from "../utils/tipCommission";
import { base64Logo } from '../constants/pdfLogo'

const calculateCommission = (amount, transactionDate = new Date()) =>
  calculateTipCommission(amount, TIP_ROLES.WAITRESS, transactionDate);

const canViewActivityLogs = (role) =>
  ["Admin", "Management"].includes(String(role || "").trim());

const canClearActivityLogs = (role) =>
  String(role || "").trim() === "Admin";

export default function WaitressTipsRecords() {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);
  const [allemployees,setallemployees]=useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [clearingActivityLogs, setClearingActivityLogs] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  // Filter states
  const [dateRange, setDateRange] = useState(defaultDateRange);
  
  
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  // Balance data state - matching backend structure
  const [balanceData, setBalanceData] = useState({
    tipPool: 0,
    addedTokes: 0,
    paidOut: 0
  });

  const [balanceLoading, setBalanceLoading] = useState(false);

  console.log("balanceData", balanceLoading);

  useEffect(() => {
    const fetchEmployees = async () => {
        try {
            const response = await employeeManagementService.getAllEmployees({ limit: 1000000 });
            if (response.success) {
                setallemployees(response?.data?.body?.data?.employees);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    fetchEmployees();
}, []);


  // Fetch balance data - wrapped in useCallback to prevent dependency issues
  const fetchBalanceData = useCallback(async () => {
    try {
      setBalanceLoading(true);
      const response = await waitresstipoutService.getBalances();

      if (response.success) {
        // Backend returns: { tipPool: total, addedTokes: tokes, paidOut: tax_collections }
        setBalanceData({
          tipPool: response.data?.tipPool || 0,
          addedTokes: response.data?.addedTokes || 0,
          paidOut: response.data?.paidOut || 0
        });
      } else {
        console.error("Failed to load balance data:", response.error);
        toast.error("Failed to load balance data");
      }
    } catch (err) {
      console.error("Error fetching balance data:", err);
      toast.error("Failed to load balance data");
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  // Fetch records with date filtering - wrapped in useCallback
  const fetchRecords = useCallback(async (startDate = null, endDate = null) => {
    try {
      const params = {};
      if (startDate && endDate) {
        params.startDate = moment.tz(startDate, "America/Chicago").format("YYYY-MM-DD");
        params.endDate = moment.tz(endDate, "America/Chicago").format("YYYY-MM-DD");
      }

      const response = await waitresstipoutService.getRecords(params);

      if (response.success && Array.isArray(response.data)) {
        setRecords(response.data);
      } else {
        console.error("Invalid records response:", response);
        setRecords([]);
      }
    } catch (err) {
      console.error("Error fetching records:", err);
      setRecords([]);
      throw err;
    }
  }, []);

  const fetchActivityLogs = useCallback(async (startDate = null, endDate = null) => {
    if (!canViewActivityLogs(currentUser?.role)) {
      setActivityLogs([]);
      return;
    }

    try {
      setActivityLogsLoading(true);
      const params = { limit: 200 };
      if (startDate && endDate) {
        params.startDate = moment.tz(startDate, "America/Chicago").format("YYYY-MM-DD");
        params.endDate = moment.tz(endDate, "America/Chicago").format("YYYY-MM-DD");
      }

      const response = await waitresstipoutService.getActivityLogs(params);
      if (response.success) {
        const logs = response.data?.data || response.data || [];
        setActivityLogs(Array.isArray(logs) ? logs : []);
      } else {
        setActivityLogs([]);
      }
    } catch (err) {
      console.error("Error fetching activity logs:", err);
      setActivityLogs([]);
    } finally {
      setActivityLogsLoading(false);
    }
  }, [currentUser?.role]);

  const handleClearActivityLogs = useCallback(async () => {
    if (!canClearActivityLogs(currentUser?.role)) return;

    try {
      setClearingActivityLogs(true);
      const payload = {};
      if (dateRange.start && dateRange.end) {
        payload.startDate = moment.tz(dateRange.start, "America/Chicago").format("YYYY-MM-DD");
        payload.endDate = moment.tz(dateRange.end, "America/Chicago").format("YYYY-MM-DD");
      }

      const response = await waitresstipoutService.clearActivityLogs(payload);
      if (response.success) {
        toast.success(response.data?.message || "Activity logs cleared");
        await fetchActivityLogs(dateRange.start, dateRange.end);
      } else {
        toast.error(response.error || "Failed to clear activity logs");
      }
    } catch (err) {
      console.error("Error clearing activity logs:", err);
      toast.error("Failed to clear activity logs");
    } finally {
      setClearingActivityLogs(false);
    }
  }, [currentUser?.role, dateRange.start, dateRange.end, fetchActivityLogs]);

  // Fixed: Moved fetchInitialData before useEffect and removed refreshFlag dependency
  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);

      await Promise.all([
        fetchBalanceData(),
        fetchRecords(dateRange.start, dateRange.end),
        fetchActivityLogs(dateRange.start, dateRange.end),
      ]);

    } catch (err) {
      console.error("Error fetching initial data:", err);
      setError("Failed to load data");
      toast.error("Failed to load waitress tips data");
    } finally {
      setIsLoading(false);
    }
  }, [fetchBalanceData, fetchRecords, fetchActivityLogs, dateRange.start, dateRange.end]);


  useEffect(() => {
    fetchRecords(dateRange.start, dateRange.end);
    fetchActivityLogs(dateRange.start, dateRange.end);
}, [dateRange, fetchRecords, fetchActivityLogs]);

// Initial data fetch - fixed dependency
useEffect(() => {
    fetchInitialData();
}, [fetchInitialData]);

  const handleAddRecord = async ({ empname, amount, owetotoast, shift }) => {
    try {
      const response = await waitresstipoutService.addRecord({
        empname,
        amountcash: parseFloat(amount),
        owetotoast: parseFloat(owetotoast) || 0,
        shift,
      });

      if (response.success && response.data && !response.data.error) {
        toast.success("Record added successfully");
        setShowAddModal(false);
        setCurrentPage(1);
        await Promise.all([
          fetchRecords(dateRange.start, dateRange.end),
          fetchBalanceData(),
          fetchActivityLogs(dateRange.start, dateRange.end),
        ]);
      } else {
        toast.error(response.error || response.data?.error || "Failed to add record");
      }
    } catch (err) {
      console.error("Error adding record:", err);
      toast.error("An error occurred while adding record");
    }
  };

  // Modified handleUpdateRecord function with immediate UI update
  const handleUpdateRecord = async ({ id, date, empname, amount, owetotoast }) => {
    try {
      const response = await waitresstipoutService.updateRecord(id, {
        date: parseAppDateTime(date)?.format("YYYY-MM-DDTHH:mm:ss") ?? date,
        empname,
        amountcash: parseFloat(amount) || 0,
        owetotoast: parseFloat(owetotoast) || 0
      });

      if (response.success && response.data && !response.data.error) {
        toast.success("Record updated successfully");
        setShowEditModal(false);
        await Promise.all([
          fetchRecords(dateRange.start, dateRange.end),
          fetchBalanceData(),
          fetchActivityLogs(dateRange.start, dateRange.end),
        ]);
      } else {
        toast.error(response.error || response.data?.error || "Failed to update record");
      }
    } catch (err) {
      console.error("Error updating record:", err);
      toast.error("An error occurred while updating record");
      await fetchRecords(dateRange.start, dateRange.end);
    }
  };

  // Modified handleDeleteRecord function with immediate UI update
  const handleDeleteRecord = async () => {
    if (!currentRecord) return false;

    try {
      setIsLoading(true);
      const { id, tip_out_cash, tax, paid_out } = currentRecord;

      // Store original records for rollback
      const originalRecords = [...records];
      const originalBalanceData = { ...balanceData };

      // Update UI immediately (optimistic update)
      setRecords(prev => prev.filter(record => record.id !== id));

      // Update balance data immediately
      setBalanceData(prev => ({
        ...prev,
        tipPool: prev.tipPool - parseFloat(tip_out_cash || 0),
        addedTokes: prev.addedTokes - parseFloat(tax || 0),
        paidOut: prev.paidOut - parseFloat(paid_out || 0)
      }));

      const response = await waitresstipoutService.deleteRecord(id);

      if (response.success) {
        toast.success("Record deleted successfully");
        await Promise.all([
          fetchRecords(dateRange.start, dateRange.end),
          fetchBalanceData(),
          fetchActivityLogs(dateRange.start, dateRange.end),
        ]);
        return true;
      } else {
        // Rollback on failure
        setRecords(originalRecords);
        setBalanceData(originalBalanceData);
        toast.error(response.error || "Failed to delete record");
        return false;
      }
    } catch (err) {
      console.error("Error deleting record:", err);
      toast.error("An error occurred while deleting record");
      // Rollback UI changes on error
      await fetchRecords(dateRange.start, dateRange.end);
      await fetchBalanceData();
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Filter records based on date range and search term
  const filteredRecords = useMemo(() => {
    if (!Array.isArray(records)) return [];

    return records.filter(record => {
      const inDateRange = isDateTimeInRange(record.date, dateRange.start, dateRange.end);

      const matchesSearch =
        !searchTerm ||
        (record.employee && record.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.added_by && record.added_by.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.updated_by && record.updated_by.toLowerCase().includes(searchTerm.toLowerCase()));

      return inDateRange && matchesSearch;
    });
  }, [records, dateRange, searchTerm]);

  // Calculate employee totals from filtered records
  const employeeTotals = useMemo(() => {
    if (!Array.isArray(filteredRecords)) return [];
  
    const totalsMap = {};
  
    filteredRecords.forEach(record => {
      // Normalize employee name: trim whitespace, convert to lowercase for grouping
      const rawEmployee = record.employee || '';
      const normalizedKey = rawEmployee.trim().toLowerCase();
      const displayName = rawEmployee.trim(); // Keep original case for display
      
      // Skip if employee name is empty
      if (!normalizedKey) return;
      
      if (!totalsMap[normalizedKey]) {
        totalsMap[normalizedKey] = {
          employee: displayName, // Use the first occurrence's display name
          tipOut: 0,
          tax: 0,
          oweToToast: 0,
          paidOut: 0
        };
      }
  
      const tipOutCash = parseFloat(record.tip_out_cash) || 0;
      const tax = parseFloat(record.tax) || 0;
      const oweToToast = parseFloat(record.owe_to_toast) || 0;
  
      totalsMap[normalizedKey].tipOut += tipOutCash;
      totalsMap[normalizedKey].tax += tax;
      totalsMap[normalizedKey].oweToToast += oweToToast;
      totalsMap[normalizedKey].paidOut += (tipOutCash - tax);
    });
  
    return Object.values(totalsMap).sort((a, b) => a.employee.localeCompare(b.employee));
  }, [filteredRecords]);
  // Format date range for display
  const formatDateRange = () => {
    return `${moment(dateRange.start).format("MM/DD/YYYY")} - ${moment(dateRange.end).format("MM/DD/YYYY")}`;
  };

  // Replace periodSummary state with useMemo
const periodSummary = useMemo(() => {
  if (!Array.isArray(filteredRecords)) {
    return { tipPool: 0, addedTokes: 0, paidOut: 0 };
  }

  return filteredRecords.reduce(
    (acc, record) => {
      const tipOutCash = parseFloat(record.tip_out_cash) || 0;
      const tax = parseFloat(record.tax) || 0;
      const oweToToast = parseFloat(record.owe_to_toast) || 0;

      return {
        tipPool: acc.tipPool + tipOutCash,
        addedTokes: acc.addedTokes + tax,
        paidOut: acc.paidOut + (tipOutCash - tax - oweToToast) // CORRECTED
      };
    },
    { tipPool: 0, addedTokes: 0, paidOut: 0 }
  );
}, [filteredRecords]);


  // Handle edit record
  const handleEditRecord = (record) => {
    setCurrentRecord(record);
    setShowEditModal(true);
  };

  useEffect(() => {
    fetchRecords(dateRange.start, dateRange.end);
}, [dateRange, fetchRecords]);

  // Handle delete record
  const handleDeleteRecordClick = (record) => {
    setCurrentRecord(record);
    setShowDeleteModal(true);
  };


  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    );
  }



  const exportWaitressTipsRecordsToPDF = () => {
    try {
      const doc = new jsPDF();

      // Set document properties
      doc.setProperties({
        title: 'Waitress Tips Report',
        subject: 'Waitress Tips Transactions',
        author: 'Elite Poker Lounge',
        creator: 'Waitress Tips System'
      });

      // Add logo (if you have base64Logo available)
      const logoWidth = 40;
      const logoHeight = 20;
      doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight);

      // Add timestamp
      const now = new Date();
      const dateTime = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date & Time: ${dateTime}`, 14, 15);
      doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

      // Title
      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text('Waitress Tips Records', 14, 40);

      // Summary section
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text('Period Summary:', 14, 50);

      doc.setFont(undefined, 'normal');
      doc.text(`Total Tip Pool: $${formatCurrency(periodSummary.tipPool)}`, 14, 60);
      doc.text(`Total Added Tokes: $${formatCurrency(periodSummary.addedTokes)}`, 14, 70);
      doc.text(`Total Paid Out: $${formatCurrency(periodSummary.paidOut)}`, 14, 80);

      // Employee Totals Section
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Employee Totals:', 14, 95);

      // Employee totals table
      const employeeHeaders = ['Employee', 'Total Tip Out', `${getTipCommissionLabel(TIP_ROLES.WAITRESS)} Total`, 'Total Paid Out'];
      const employeeData = employeeTotals.map(employee => [
        employee.employee,
        `$${formatCurrency(employee.tipOut)}`,
        `$${formatCurrency(employee.tax)}`,
        `$${formatCurrency(employee.paidOut)}`
      ]);

      // Add employee totals row
      employeeData.push([
        'TOTAL',
        `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.tipOut, 0))}`,
        `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.tax, 0))}`,
        `$${formatCurrency(employeeTotals.reduce((sum, emp) => sum + emp.paidOut, 0))}`
      ]);

      let currentY = 100;

      if (employeeTotals.length > 0) {
        autoTable(doc, {
          head: [employeeHeaders],
          body: employeeData,
          startY: currentY,
          theme: 'plain',
          styles: {
            fontSize: 9,
            cellPadding: 3,
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.1
          },
          headStyles: {
            fillColor: false,
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
          },
          bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0]
          },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 35, halign: 'right' },
            2: { cellWidth: 35, halign: 'right' },
            3: { cellWidth: 35, halign: 'right' }
          },
          margin: { left: 14, right: 14 },
          tableLineColor: [0, 0, 0],
          tableLineWidth: 0.1,
          didParseCell: function (data) {
            // Make the total row bold
            if (data.row.index === employeeData.length - 1) {
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        currentY = doc.lastAutoTable.finalY + 15;
      }

      // Transaction Details Section
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Transaction Details:', 14, currentY);

      // Prepare transaction table data
      const transactionHeaders = [
        'Date',
        'Employee',
        'Tip Out Cash',
        getTipCommissionLabel(TIP_ROLES.WAITRESS),
        'Paid Out',
        'Added By',
        'Updated By'
      ];

      const transactionData = filteredRecords.map(record => [
        moment(record.date, "MM/DD/YYYY hh:mm A", true).isValid()
          ? record.date
          : formatInAppTz(record.date),
        record.employee || 'N/A',
        `$${formatCurrency(record.tip_out_cash)}`,
        `$${formatCurrency(record.tax)}`,
        `$${formatCurrency(record.paid_out)}`,
        record.added_by || 'N/A',
        record.updated_by || 'N/A'
      ]);

      // Add transaction details table
      autoTable(doc, {
        head: [transactionHeaders],
        body: transactionData,
        startY: currentY + 5,
        theme: 'plain',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 30 },
          2: { cellWidth: 25, halign: 'right' },
          3: { cellWidth: 25, halign: 'right' },
          4: { cellWidth: 25, halign: 'right' },
          5: { cellWidth: 30 }
        },
        margin: { left: 14, right: 14 },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1
      });

      // Save the PDF
      const fileName = `Waitress_Tips_Report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
      doc.save(fileName);

      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="bg-gray-50 p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Waitress Tips Records</h1>
        <p className="text-gray-600 mt-2">Manage and track waitress tip distributions</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">Tip Pool</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                ${formatCurrency(periodSummary.tipPool)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">Added Tokes</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                ${formatCurrency(periodSummary.addedTokes)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">Paid Out</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                ${formatCurrency(periodSummary.paidOut)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Add Record Button */}
      <div className="flex justify-end mb-6">
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-yellow-500 hover:bg-yellow-600 text-black"
          disabled={isLoading}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Record
        </Button>
      </div>

      {/* Filter Controls */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <Button
              variant="outline"
              onClick={() => setIsDatePickerOpen(true)}
              className="w-full justify-start text-left font-normal"
            >
              <Calendar className="mr-2 h-4 w-4" />
              {formatDateRange()}
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by employee or added by..."
            />
          </div>
        </div>
      </div>

      {/* Records Table */}
      <Card className="bg-white shadow rounded-lg mb-8">

        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold text-gray-800">
              Transaction History ({filteredRecords.length} records)
            </CardTitle>
            <Button
              onClick={exportWaitressTipsRecordsToPDF}
              variant="outline"
              className="bg-yellow-400 hover:bg-yellow-500 text-black"
            >
              Export to PDF
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <WaitressTipsTransactionTable
            records={filteredRecords}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            entriesPerPage={entriesPerPage}
            setEntriesPerPage={setEntriesPerPage}
            onEdit={handleEditRecord}
            onDelete={handleDeleteRecordClick}
            currentUser={currentUser}
            dateRange={dateRange}
            employeeApiService={employeeManagementService}
            employees={allemployees}
          />
        </CardContent>
      </Card>

      {canViewActivityLogs(currentUser?.role) && (
        <WaitressTipActivityLogPanel
          logs={activityLogs}
          isLoading={activityLogsLoading}
          canClear={canClearActivityLogs(currentUser?.role)}
          onClearLogs={handleClearActivityLogs}
          clearing={clearingActivityLogs}
        />
      )}

      {/* Employee Totals Section */}
      <Card className="bg-white shadow rounded-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-800">
          Overall Total Tips Received
          ({formatDateRange()})
          </CardTitle>
          <Button
            onClick={() => fetchRecords()}
            variant="outline"
            size="sm"
            disabled={isLoading}
          >
            {isLoading ? "Refreshing..." : "Refresh"}
          </Button>
        </CardHeader>
        <CardContent>
          <EmployeeTotalsTable
          employeeTotals={employeeTotals}
          dateRange={dateRange}
          employeeApiService={employeeManagementService}
          employees={allemployees}
          tipRole={TIP_ROLES.WAITRESS}
          />
        </CardContent>
      </Card>

      {/* Modals */}
      <WaitressTipAddModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddRecord}
        employees={allemployees}
        calculateCommission={calculateCommission}
      />

      <WaitressTipEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        record={currentRecord}
        onUpdate={handleUpdateRecord}
        employees={allemployees}
        calculateCommission={calculateCommission}
        currentUser={currentUser}
      />

      <WaitressTipDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        record={currentRecord}
        onDelete={handleDeleteRecord}
      />

      {/* Date Range Picker */}
      <DateRangePicker
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        onApply={(start, end) => {
          setDateRange({
            start: startOfDay(start),
            end: endOfDay(end)
          });
          setIsDatePickerOpen(false);
        }}
        initialStartDate={dateRange.start}
        initialEndDate={dateRange.end}
      />
    </div>
  );
}